import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import SignaturePad from 'react-signature-canvas';

import { useUpdateMyProfileModalStore } from './UpdateMyProfileModalStore';

import { getProfile } from 'api/profile/getProfile';
import useUpdateSignature from 'hooks/Profile/useUpdateSignature';
import { useUpdateProfile } from 'hooks/Profile/useUpdateProfile';
import { updateProfileSchema, UpdateProfileFormValues } from 'api/profile/schema';
import { UpdateProfileData } from 'api/profile/type';
import { MLButton } from 'components/Button/Button';
import { MLInput } from 'components/Input/Input';

interface UpdateMyProfileModalProps {
  refetch: () => void;
}

const UpdateMyProfileModal: React.FC<UpdateMyProfileModalProps> = ({ refetch }) => {
  const { t } = useTranslation();
  const { isOpen, onUpdateMyProfileModalClose, userData, setUserData } = useUpdateMyProfileModalStore();
  const { updateProfileMutation, isLoading } = useUpdateProfile();
  const { updateSignatureMutation, isSignatureUpdating } = useUpdateSignature();
  const queryClient = useQueryClient();

  const [isEditingSignature, setIsEditingSignature] = useState(false);

  const sigCanvas = useRef<SignaturePad | null>(null);

  const saveSignature = () => {
    if (!sigCanvas.current) {
      console.warn('Signature pad is not initialized');
      return;
    }

    if (sigCanvas.current.isEmpty()) {
      enqueueSnackbar('Signature pad is empty', { variant: 'warning' });
      return;
    }

    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');

    updateSignatureMutation(
      { signature: signatureData },
      {
        onSuccess: async () => {
          try {
            const freshUserData = await getProfile();
            setUserData(freshUserData);
            queryClient.setQueryData('profile', freshUserData);
            sigCanvas.current?.clear();
            setIsEditingSignature(false);
          } catch (error) {
            enqueueSnackbar('Failed to refresh profile data', { variant: 'error' });
          }
        },
      },
    );
  };

  useEffect(() => {
    if (isOpen) {
      sigCanvas.current?.clear();
      if (userData && userData.user_signature) {
        setIsEditingSignature(false);
      } else {
        setIsEditingSignature(true);
      }
    }
  }, [isOpen, userData]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<UpdateProfileFormValues>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone_number: '',
      email: '',
      dob: '',
      avatar: null,
    },
    mode: 'onChange',
  });

  // Reset form with userData when it becomes available
  useEffect(() => {
    if (userData && Object.keys(userData).length > 0) {
      reset({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        phone_number: userData.phone_number || '',
        email: userData.email || '',
        dob: userData.dob || '',
        avatar: null,
      });
    }
  }, [userData, reset]);

  const onSubmit = useCallback(
    (data: UpdateProfileFormValues) => {
      const profileData: UpdateProfileData = {
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number || '',
        email: data.email || '',
        dob: data.dob || '',
        avatar: data.avatar instanceof File ? data.avatar : null,
      };

      updateProfileMutation(profileData, {
        onSuccess: () => {
          onUpdateMyProfileModalClose();
          refetch();
          reset();
        },
      });
    },
    [updateProfileMutation, onUpdateMyProfileModalClose, refetch, reset],
  );

  const onReset = useCallback(() => {
    reset();
    onUpdateMyProfileModalClose();
    refetch();
  }, [reset, onUpdateMyProfileModalClose, refetch]);

  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} toggle={onUpdateMyProfileModalClose} size="lg">
      <ModalHeader className="border-bottom py-3" toggle={onUpdateMyProfileModalClose}>
        Update My Profile
      </ModalHeader>
      <ModalBody className="px-4 py-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <MLInput
                    {...field}
                    label="First Name"
                    placeholder="Enter first name"
                    error={errors.first_name?.message}
                    required
                  />
                )}
              />
            </div>
            <div className="col-md-6 mb-3">
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <MLInput
                    {...field}
                    label="Last Name"
                    placeholder="Enter last name"
                    error={errors.last_name?.message}
                    required
                  />
                )}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <Controller
                name="email"
                control={control}
                disabled
                render={({ field }) => (
                  <MLInput {...field} label="Email" placeholder="Enter email" error={errors.email?.message} />
                )}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Controller
                name="phone_number"
                control={control}
                render={({ field }) => (
                  <MLInput
                    {...field}
                    label="Phone Number"
                    placeholder="Enter phone number"
                    error={errors.phone_number?.message}
                  />
                )}
              />
            </div>
            <div className="col-md-6 mb-3">
              <Controller
                name="dob"
                control={control}
                render={({ field }) => (
                  <MLInput {...field} type="date" label="Date of Birth" error={errors.dob?.message} />
                )}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Signature</label>
              {isEditingSignature ? (
                <div>
                  <div className="signature-wrapper border rounded p-2">
                    <SignaturePad ref={sigCanvas} canvasProps={{ className: 'signatureCanvas' }} />
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-2">
                    {userData.user_signature && (
                      <MLButton
                        isGhost
                        color="secondary"
                        onClick={() => setIsEditingSignature(false)}
                        innerText="Cancel"
                        fontSize={14}
                      />
                    )}
                    <MLButton
                      isGhost
                      color="warning"
                      onClick={() => sigCanvas.current?.clear()}
                      innerText="Clear"
                      fontSize={14}
                    />
                    <MLButton
                      isGhost
                      color="success"
                      onClick={saveSignature}
                      innerText={isSignatureUpdating ? 'Saving...' : 'Save Signature'}
                      fontSize={14}
                      disabled={isSignatureUpdating}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  {userData.user_signature && (
                    <img
                      src={`${process.env.REACT_APP_BASE_DJANGO_URL}${userData.user_signature}`}
                      alt="User signature"
                      className="border rounded p-2"
                      style={{ maxHeight: '150px', width: '100%', objectFit: 'contain' }}
                    />
                  )}
                  <MLButton
                    isGhost
                    color="primary"
                    onClick={() => setIsEditingSignature(true)}
                    innerText="Update Signature"
                    fontSize={14}
                    classNames="mt-2"
                  />
                </div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <Controller
                name="avatar"
                control={control}
                render={({ field }) => (
                  <div>
                    {/* Show existing avatar if available */}
                    {userData?.avatar && (
                      <div className="mb-3">
                        <label className="form-label mb-2">Profile Picture</label>
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={`${process.env.REACT_APP_BASE_DJANGO_URL}${userData.avatar}`}
                            alt="Current profile"
                            className="rounded-circle"
                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                          />
                          <small className="text-muted">This is your current picture. You can change it below.</small>
                        </div>
                      </div>
                    )}

                    {/* Show preview of newly selected image */}
                    {field.value instanceof File && (
                      <div className="mb-3">
                        <label className="form-label mb-2">New Profile Picture Preview</label>
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={URL.createObjectURL(field.value)}
                            alt="New profile preview"
                            className="rounded-circle"
                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                          />
                          <small className="text-success">This image will replace your current profile picture</small>
                        </div>
                      </div>
                    )}

                    <MLInput
                      type="file"
                      label="Change picture (optional)"
                      accept="image/*"
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        field.onChange(file);
                      }}
                      error={errors.avatar?.message}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter className="border-top pt-3 ">
        <Button className="btn btn-ghost-danger" onClick={onReset} disabled={isLoading}>
          {' '}
          {t('Modals.delete-modal.cancel')}
        </Button>
        <Button className="btn btn-ghost-success" onClick={handleSubmit(onSubmit)} disabled={!isValid || isLoading}>
          {isLoading ? 'Updating...' : 'Save Changes'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UpdateMyProfileModal;
